/**
 * Spectrum Product Tabs Carousel
 *
 * Auto-binds to every `[data-sptc]` instance on the page:
 *   - Click a tab → swap which panel is visible.
 *   - Active panel has a horizontally-scrolling card list with snap-points.
 *   - Pagination dots track the dominant visible slide.
 *   - Prev/Next arrows scroll by one card width + gap; disable at scroll bounds.
 *
 * Idempotent — calling init multiple times on the same root is a no-op.
 * Picks up dynamically-inserted instances via MutationObserver.
 *
 * Wrapped in an IIFE so top-level `const`s don't collide with other
 * classic scripts (e.g. spectrum-plp-card-carousel.js declares its own
 * CAROUSEL_SELECTOR / DOTS_SELECTOR identifiers).
 */

(function () {
const ROOT_SELECTOR = "[data-sptc]";
const TRACK_SELECTOR = "[data-sptc-track]";
const DOTS_SELECTOR = "[data-sptc-dots]";

const prefersReducedMotion = () =>
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true;

const scrollBehavior = () => (prefersReducedMotion() ? "auto" : "smooth");

/* ---------- Tab switching ---------- */

const initTabs = (root) => {
  const tabs = root.querySelectorAll(".sptc__tab");
  const panels = root.querySelectorAll(".sptc__panel");
  if (!tabs.length || !panels.length) return;

  tabs.forEach((tab) => {
    tab.addEventListener("click", () => {
      const idx = tab.getAttribute("data-tab-index");
      tabs.forEach((t) => {
        const active = t === tab;
        t.classList.toggle("is-active", active);
        t.setAttribute("aria-selected", active ? "true" : "false");
      });
      panels.forEach((p) => {
        const active = p.getAttribute("data-panel-index") === idx;
        p.classList.toggle("is-active", active);
        if (active) {
          p.removeAttribute("hidden");
          // Re-sync dots/arrows now that the panel is visible (offsetWidth was 0 while hidden).
          const track = p.querySelector(TRACK_SELECTOR);
          if (track) syncNav(track);
        } else {
          p.setAttribute("hidden", "");
        }
      });
    });
  });
};

/* ---------- Carousel: dots + arrows ---------- */

const getCardStep = (track) => {
  const first = track.querySelector(".sptc__item");
  if (!first) return track.clientWidth || 1;
  const gap = parseInt(window.getComputedStyle(track).columnGap || "0", 10) || 0;
  return first.offsetWidth + gap;
};

const getPageCount = (track) => {
  const items = track.querySelectorAll(".sptc__item");
  if (!items.length) return 0;
  const step = getCardStep(track);
  const visible = Math.max(1, Math.round(track.clientWidth / step));
  return Math.max(1, Math.ceil(items.length / visible));
};

const getCurrentPage = (track) => {
  const step = getCardStep(track);
  const visible = Math.max(1, Math.round(track.clientWidth / step));
  const pageWidth = step * visible;
  if (pageWidth <= 0) return 0;
  return Math.round(track.scrollLeft / pageWidth);
};

const renderDots = (dotsEl, pageCount, activeIndex) => {
  if (!dotsEl) return;
  const existing = dotsEl.querySelectorAll(".sptc__dot");
  if (existing.length !== pageCount) {
    dotsEl.innerHTML = "";
    for (let i = 0; i < pageCount; i += 1) {
      const dot = document.createElement("button");
      dot.type = "button";
      dot.className = "sptc__dot" + (i === activeIndex ? " is-active" : "");
      dot.setAttribute("role", "tab");
      dot.setAttribute("aria-label", `Scroll to set ${i + 1} of ${pageCount}`);
      dot.setAttribute("aria-selected", i === activeIndex ? "true" : "false");
      dot.setAttribute("data-page-index", String(i));
      dotsEl.appendChild(dot);
    }
  } else {
    existing.forEach((dot, i) => {
      const active = i === activeIndex;
      dot.classList.toggle("is-active", active);
      dot.setAttribute("aria-selected", active ? "true" : "false");
    });
  }
};

const updateArrows = (panel, track) => {
  const prev = panel.querySelector('[data-sptc-arrow="prev"]');
  const next = panel.querySelector('[data-sptc-arrow="next"]');
  if (!prev || !next) return;
  const max = track.scrollWidth - track.clientWidth - 1;
  prev.setAttribute("aria-disabled", track.scrollLeft <= 1 ? "true" : "false");
  next.setAttribute("aria-disabled", track.scrollLeft >= max ? "true" : "false");
};

const syncNav = (track) => {
  const panel = track.closest(".sptc__panel");
  if (!panel) return;
  const dotsEl = panel.querySelector(DOTS_SELECTOR);
  const pageCount = getPageCount(track);
  // Only render dots if there's more than one page worth of cards.
  if (pageCount <= 1) {
    if (dotsEl) dotsEl.innerHTML = "";
  } else {
    const active = Math.min(pageCount - 1, Math.max(0, getCurrentPage(track)));
    renderDots(dotsEl, pageCount, active);
  }
  updateArrows(panel, track);
};

const initCarousel = (track) => {
  if (track.dataset.sptcCarouselReady === "true") return;
  track.dataset.sptcCarouselReady = "true";

  // Initial sync.
  syncNav(track);

  // Re-sync on scroll (rAF-throttled).
  let scrollScheduled = false;
  track.addEventListener(
    "scroll",
    () => {
      if (scrollScheduled) return;
      scrollScheduled = true;
      requestAnimationFrame(() => {
        scrollScheduled = false;
        syncNav(track);
      });
    },
    { passive: true }
  );

  // Re-sync on resize (debounced).
  let resizeTimer = null;
  window.addEventListener("resize", () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => syncNav(track), 150);
  });

  // Dot clicks (delegated to dots container).
  const panel = track.closest(".sptc__panel");
  const dotsEl = panel?.querySelector(DOTS_SELECTOR);
  dotsEl?.addEventListener("click", (e) => {
    const dot = e.target.closest(".sptc__dot");
    if (!dot) return;
    e.preventDefault();
    const pageIndex = Number(dot.getAttribute("data-page-index") || 0);
    const step = getCardStep(track);
    const visible = Math.max(1, Math.round(track.clientWidth / step));
    track.scrollTo({ left: step * visible * pageIndex, behavior: scrollBehavior() });
  });

  // Arrow clicks.
  panel?.querySelectorAll("[data-sptc-arrow]").forEach((arrow) => {
    arrow.addEventListener("click", () => {
      if (arrow.getAttribute("aria-disabled") === "true") return;
      const dir = arrow.getAttribute("data-sptc-arrow") === "next" ? 1 : -1;
      const step = getCardStep(track);
      track.scrollBy({ left: dir * step, behavior: scrollBehavior() });
    });
  });
};

/* ---------- Init helpers ---------- */

const initRoot = (root) => {
  if (root.dataset.sptcReady === "true") return;
  root.dataset.sptcReady = "true";
  initTabs(root);
  root.querySelectorAll(TRACK_SELECTOR).forEach(initCarousel);
};

const initAll = (scope = document) => {
  scope.querySelectorAll(ROOT_SELECTOR).forEach(initRoot);
};

/* ---------- Boot + watch for dynamically inserted instances ---------- */

const mo = new MutationObserver((mutations) => {
  for (const m of mutations) {
    for (const node of m.addedNodes) {
      if (!(node instanceof HTMLElement)) continue;
      if (node.matches?.(ROOT_SELECTOR)) {
        initRoot(node);
      } else {
        node.querySelectorAll?.(ROOT_SELECTOR).forEach(initRoot);
      }
    }
  }
});

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => {
    initAll();
    mo.observe(document.body, { childList: true, subtree: true });
  });
} else {
  initAll();
  mo.observe(document.body, { childList: true, subtree: true });
}
})();
